import { useMemo } from 'react';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import { MERCHANT_LISTING_KIND, MERCHANT_ACTIVITY_KIND } from '@/lib/merchantNostr';
import { cn } from '@/lib/utils';

/** Extract a tag value by name */
function getTag(event: NostrEvent, name: string): string | undefined {
  return event.tags.find(([n]) => n === name)?.[1];
}

/** Friendly time-ago string */
function timeAgo(ts: number): string {
  const seconds = Math.floor(Date.now() / 1000) - ts;
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return new Date(ts * 1000).toLocaleDateString();
}

/** Category label map */
const CATEGORY_LABELS: Record<string, string> = {
  cafe: 'Cafe',
  restaurant: 'Restaurant',
  bar: 'Bar / Pub',
  retail: 'Retail',
  grocery: 'Grocery',
  'food-truck': 'Food Truck',
  bakery: 'Bakery',
  salon: 'Salon',
  fitness: 'Fitness',
  services: 'Services',
  tech: 'Tech',
  travel: 'Travel',
  other: 'Other',
};

/** Category emoji map */
const CATEGORY_EMOJI: Record<string, string> = {
  cafe: '☕',
  restaurant: '🍽️',
  bar: '🍺',
  retail: '🛍️',
  grocery: '🥬',
  'food-truck': '🚚',
  bakery: '🥐',
  salon: '💇',
  fitness: '💪',
  services: '💼',
  tech: '💻',
  travel: '✈️',
  other: '🏪',
};

/**
 * Activity status derived from Nostr heartbeat events:
 * - 'active'  — has a bch-payment heartbeat within 7 days (real BCH transactions)
 * - 'listed'  — has an app-open heartbeat within 7 days, but no payment signal yet
 * - 'new'     — no heartbeat at all (just published their listing)
 */
export type MerchantStatus = 'active' | 'listed' | 'new';

interface MerchantInfo {
  pubkey: string;
  name: string;
  description: string;
  category: string;
  website?: string;
  geohash?: string;
  listingAt: number;
  lastActive: number | null;
  lastPayment: number | null;
  status: MerchantStatus;
}

export function MerchantDirectoryPage() {
  const { nostr } = useNostr();

  // Step 1: Fetch all merchant listings
  const { data: listings, isLoading: loadingListings } = useQuery({
    queryKey: ['nostr', 'merchant-listings'],
    queryFn: async (c) => {
      return nostr.query(
        [{ kinds: [MERCHANT_LISTING_KIND], '#t': ['bch-merchant'], limit: 100 }],
        { signal: c.signal },
      );
    },
    staleTime: 30_000,
  });

  // Step 2: Once we have listings, fetch kind 5 deletions from those same authors
  const listingAuthors = useMemo(() => {
    if (!listings?.length) return [];
    return [...new Set(listings.map((e) => e.pubkey))];
  }, [listings]);

  const { data: deletions, isLoading: loadingDeletions } = useQuery({
    queryKey: ['nostr', 'merchant-deletions', listingAuthors],
    queryFn: async (c) => {
      if (!listingAuthors.length) return [];
      return nostr.query(
        [{ kinds: [5], authors: listingAuthors, limit: 200 }],
        { signal: c.signal },
      );
    },
    enabled: listingAuthors.length > 0,
    staleTime: 60_000,
  });

  // Step 3: Fetch recent heartbeats (last 7 days) — all activity + payment-specific
  const since = useMemo(() => Math.floor(Date.now() / 1000) - 7 * 86400, []);
  const { data: heartbeats, isLoading: loadingHeartbeats } = useQuery({
    queryKey: ['nostr', 'merchant-heartbeats', since],
    queryFn: async (c) => {
      // Single query — we'll split by tag in JS
      return nostr.query(
        [{ kinds: [MERCHANT_ACTIVITY_KIND], '#t': ['bch-merchant-activity'], since, limit: 500 }],
        { signal: c.signal },
      );
    },
    staleTime: 60_000,
  });

  // Build the merged merchant list
  const merchants = useMemo<MerchantInfo[]>(() => {
    if (!listings) return [];

    // Build a set of deleted coordinates from kind 5 events
    const deletedCoords = new Map<string, number>(); // "a" tag value → deletion created_at
    if (deletions) {
      for (const del of deletions) {
        for (const [tagName, tagVal] of del.tags) {
          if (tagName === 'a' && tagVal) {
            // Only trust deletions where the `a` tag pubkey matches the event pubkey
            const parts = tagVal.split(':');
            if (parts[1] === del.pubkey) {
              const prev = deletedCoords.get(tagVal) ?? 0;
              if (del.created_at > prev) deletedCoords.set(tagVal, del.created_at);
            }
          }
        }
      }
    }

    // Latest heartbeat per pubkey
    const latestHeartbeat = new Map<string, number>();
    if (heartbeats) {
      for (const hb of heartbeats) {
        const prev = latestHeartbeat.get(hb.pubkey) ?? 0;
        if (hb.created_at > prev) latestHeartbeat.set(hb.pubkey, hb.created_at);
      }
    }

    // Deduplicate listings by pubkey (keep newest)
    const byPubkey = new Map<string, NostrEvent>();
    for (const ev of listings) {
      const existing = byPubkey.get(ev.pubkey);
      if (!existing || ev.created_at > existing.created_at) {
        byPubkey.set(ev.pubkey, ev);
      }
    }

    // Known test pubkeys from development — filter these out
    const TEST_PUBKEYS = new Set([
      'd40bf69b8216a6de7bd2f2cb80663fba7d3c7855aeda15a0cb07f9d12cb91b8c',
      'a4af2cc9abc05bd3adff4650c4a0627704519c769adb3a29b545a9142b1c8979',
      '437a2c083dee89afb366c5701b38a5218ac924f4942df8430c0a40ee2160837b',
    ]);

    // Filter out deleted, tombstone, test, and invalid listings
    for (const [pubkey, ev] of byPubkey) {
      // Block known test pubkeys
      if (TEST_PUBKEYS.has(pubkey)) {
        byPubkey.delete(pubkey);
        continue;
      }

      // Check for tombstone: has a "deleted" tag
      const isTombstone = ev.tags.some(([n]) => n === 'deleted');
      if (isTombstone) {
        byPubkey.delete(pubkey);
        continue;
      }

      // Validate: must have a title and the bch-merchant tag
      const hasTitle = ev.tags.some(([n]) => n === 'title');
      const hasMerchantTag = ev.tags.some(([n, v]) => n === 't' && v === 'bch-merchant');
      if (!hasTitle || !hasMerchantTag) {
        byPubkey.delete(pubkey);
        continue;
      }

      // Check for kind 5 deletion
      const dTag = ev.tags.find(([n]) => n === 'd')?.[1] ?? '';
      const coord = `${MERCHANT_LISTING_KIND}:${pubkey}:${dTag}`;
      const deletedAt = deletedCoords.get(coord);
      if (deletedAt && deletedAt >= ev.created_at) {
        byPubkey.delete(pubkey);
      }
    }

    return Array.from(byPubkey.values()).map((ev) => {
      const tTags = ev.tags.filter(([n]) => n === 't').map(([, v]) => v);
      const category = tTags.find((t) => t !== 'bch-merchant') ?? '';
      return {
        pubkey: ev.pubkey,
        name: getTag(ev, 'title') ?? 'Unknown Merchant',
        description: getTag(ev, 'summary') ?? ev.content ?? '',
        category,
        website: getTag(ev, 'r'),
        geohash: getTag(ev, 'g'),
        listingAt: ev.created_at,
        lastActive: latestHeartbeat.get(ev.pubkey) ?? null,
      };
    }).sort((a, b) => {
      // Active merchants first, then by most recent activity
      if (a.lastActive && !b.lastActive) return -1;
      if (!a.lastActive && b.lastActive) return 1;
      if (a.lastActive && b.lastActive) return b.lastActive - a.lastActive;
      return b.listingAt - a.listingAt;
    });
  }, [listings, deletions, heartbeats]);

  const isLoading = loadingListings || loadingDeletions || loadingHeartbeats;
  const activeMerchants = merchants.filter((m) => m.lastActive !== null);
  const inactiveMerchants = merchants.filter((m) => m.lastActive === null);

  return (
    <div className="min-h-[100dvh] bg-zinc-50 dark:bg-zinc-950">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-600 via-indigo-600 to-purple-700 px-5 py-6 pb-8">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <span className="text-2xl">📡</span>
            </div>
            <div>
              <h1 className="text-white font-black text-lg">BCH Merchant Directory</h1>
              <p className="text-white/70 text-xs">Live from the Nostr network</p>
            </div>
          </div>
          <p className="text-white/60 text-xs mt-3 leading-relaxed">
            This page subscribes to kind {MERCHANT_LISTING_KIND} merchant listings and kind {MERCHANT_ACTIVITY_KIND} activity heartbeats published by BCHPAY.Cash merchants.
          </p>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 -mt-4 pb-8 space-y-4">
        {/* Stats bar */}
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-white dark:bg-zinc-800 rounded-2xl px-4 py-3 border border-zinc-100 dark:border-zinc-700 text-center shadow-sm">
            <p className="text-xl font-black text-zinc-900 dark:text-zinc-50">
              {isLoading ? '—' : merchants.length}
            </p>
            <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">Total</p>
          </div>
          <div className="bg-white dark:bg-zinc-800 rounded-2xl px-4 py-3 border border-zinc-100 dark:border-zinc-700 text-center shadow-sm">
            <p className="text-xl font-black text-[#0AC18E]">
              {isLoading ? '—' : activeMerchants.length}
            </p>
            <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">Active</p>
          </div>
          <div className="bg-white dark:bg-zinc-800 rounded-2xl px-4 py-3 border border-zinc-100 dark:border-zinc-700 text-center shadow-sm">
            <p className="text-xl font-black text-zinc-400">
              {isLoading ? '—' : inactiveMerchants.length}
            </p>
            <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">Inactive</p>
          </div>
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white dark:bg-zinc-800 rounded-2xl p-4 border border-zinc-100 dark:border-zinc-700 animate-pulse">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-zinc-200 dark:bg-zinc-700" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-32 bg-zinc-200 dark:bg-zinc-700 rounded" />
                    <div className="h-3 w-20 bg-zinc-100 dark:bg-zinc-800 rounded" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!isLoading && merchants.length === 0 && (
          <div className="bg-white dark:bg-zinc-800 rounded-2xl border-2 border-dashed border-zinc-200 dark:border-zinc-700 px-6 py-12 text-center">
            <span className="text-4xl block mb-3">📡</span>
            <p className="text-sm font-bold text-zinc-700 dark:text-zinc-200 mb-1">No merchants found yet</p>
            <p className="text-xs text-zinc-400 max-w-xs mx-auto leading-relaxed">
              Enable "Merchant Discovery" in{' '}
              <a href="/app" className="text-purple-500 underline">BCHPAY.Cash Settings</a>{' '}
              to be the first merchant listed on the network.
            </p>
          </div>
        )}

        {/* Active merchants */}
        {activeMerchants.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2 px-1">
              <span className="w-2 h-2 rounded-full bg-[#0AC18E] animate-pulse" />
              <p className="text-xs font-bold text-[#0AC18E] uppercase tracking-wider">
                Active Merchants ({activeMerchants.length})
              </p>
            </div>
            <div className="space-y-2">
              {activeMerchants.map((m) => (
                <MerchantCard key={m.pubkey} merchant={m} />
              ))}
            </div>
          </div>
        )}

        {/* Inactive merchants */}
        {inactiveMerchants.length > 0 && (
          <div>
            <p className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2 px-1">
              No Recent Activity ({inactiveMerchants.length})
            </p>
            <div className="space-y-2">
              {inactiveMerchants.map((m) => (
                <MerchantCard key={m.pubkey} merchant={m} />
              ))}
            </div>
          </div>
        )}

        {/* Protocol info */}
        <div className="bg-zinc-100 dark:bg-zinc-900 rounded-2xl p-4 border border-zinc-200 dark:border-zinc-800">
          <p className="text-xs font-bold text-zinc-500 dark:text-zinc-400 mb-2">How it works</p>
          <div className="space-y-2 text-xs text-zinc-500 dark:text-zinc-400 leading-relaxed">
            <p>
              <span className="font-mono font-bold text-purple-500">kind {MERCHANT_LISTING_KIND}</span>{' '}
              — Addressable merchant listing with name, category, and optional location. Published once, updated as needed.
            </p>
            <p>
              <span className="font-mono font-bold text-purple-500">kind {MERCHANT_ACTIVITY_KIND}</span>{' '}
              — Activity heartbeat. Published max once per 24h to signal the merchant is actively accepting BCH.
            </p>
            <p>
              Filter: <span className="font-mono bg-zinc-200 dark:bg-zinc-800 px-1.5 py-0.5 rounded">#t = bch-merchant</span>
            </p>
          </div>
        </div>

        {/* Back link */}
        <div className="text-center pt-2">
          <a
            href="/app"
            className="text-xs font-semibold text-purple-500 hover:text-purple-600 transition-colors"
          >
            ← Back to BCHPAY.Cash
          </a>
        </div>
      </div>
    </div>
  );
}

function MerchantCard({ merchant }: { merchant: MerchantInfo }) {
  const isActive = merchant.lastActive !== null;
  const isRecentlyActive = merchant.lastActive !== null &&
    (Date.now() / 1000 - merchant.lastActive) < 86400;

  const emoji = CATEGORY_EMOJI[merchant.category] ?? '🏪';
  const categoryLabel = CATEGORY_LABELS[merchant.category] ?? merchant.category;

  return (
    <div className={cn(
      'bg-white dark:bg-zinc-800 rounded-2xl border overflow-hidden transition-all',
      isRecentlyActive
        ? 'border-[#0AC18E]/30 shadow-sm shadow-[#0AC18E]/10'
        : 'border-zinc-100 dark:border-zinc-700'
    )}>
      <div className="p-4 flex items-center gap-3">
        {/* Icon */}
        <div className={cn(
          'w-12 h-12 rounded-xl flex items-center justify-center shrink-0 text-2xl',
          isActive
            ? 'bg-[#0AC18E]/10 dark:bg-[#0AC18E]/15'
            : 'bg-zinc-100 dark:bg-zinc-900'
        )}>
          {emoji}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-bold text-zinc-900 dark:text-zinc-50 truncate">
              {merchant.name}
            </p>
            {isRecentlyActive && (
              <span className="w-2 h-2 rounded-full bg-[#0AC18E] shrink-0" />
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            {categoryLabel && (
              <span className="text-[10px] font-bold text-purple-500 bg-purple-50 dark:bg-purple-950/30 px-1.5 py-0.5 rounded-full">
                {categoryLabel}
              </span>
            )}
            {merchant.geohash && (
              <span className="text-[10px] font-mono text-zinc-400 bg-zinc-100 dark:bg-zinc-900 px-1.5 py-0.5 rounded-full">
                📍 {merchant.geohash}
              </span>
            )}
          </div>
          {merchant.description && (
            <p className="text-xs text-zinc-400 mt-1 truncate">{merchant.description}</p>
          )}
        </div>

        {/* Last active */}
        <div className="text-right shrink-0">
          {isActive ? (
            <div>
              <p className={cn(
                'text-xs font-bold',
                isRecentlyActive ? 'text-[#0AC18E]' : 'text-zinc-400'
              )}>
                {timeAgo(merchant.lastActive!)}
              </p>
              <p className="text-[10px] text-zinc-400 mt-0.5">last active</p>
            </div>
          ) : (
            <div>
              <p className="text-xs font-bold text-zinc-300 dark:text-zinc-600">—</p>
              <p className="text-[10px] text-zinc-300 dark:text-zinc-600 mt-0.5">no heartbeat</p>
            </div>
          )}
        </div>
      </div>

      {/* Website link */}
      {merchant.website && (
        <div className="border-t border-zinc-100 dark:border-zinc-700 px-4 py-2">
          <a
            href={merchant.website}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-purple-500 hover:text-purple-600 truncate block"
          >
            {merchant.website.replace(/^https?:\/\//, '')} →
          </a>
        </div>
      )}
    </div>
  );
}
